/**
 * Helpers for turning the keyed-distribution objects returned by the
 * analytics API (e.g. `{ FREE: 169, PREMIUM: 21 }`) into the array shape
 * Recharts expects (`[{ name, value }]`).
 */

export interface DistributionPoint {
  name: string;
  value: number;
}

/**
 * Convert a Record-shaped distribution into a Recharts-friendly array.
 * - `null` / `undefined` inputs return `[]`.
 * - Non-numeric / non-finite values are coerced to `0`.
 * - Zero-value entries are kept by default; pass `{ skipZero: true }` to drop them.
 * - Output is sorted by descending value for a stable visual order.
 */
export function recordToChartData(
  record: Partial<Record<string, number>> | undefined | null,
  options: { skipZero?: boolean } = {},
): DistributionPoint[] {
  if (!record) return [];
  const points: DistributionPoint[] = [];
  for (const [name, raw] of Object.entries(record)) {
    const value = typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;
    if (options.skipZero && value === 0) continue;
    points.push({ name, value });
  }
  return points.sort((a, b) => b.value - a.value);
}

/** Sum every value in a distribution; tolerates `undefined`/`null`. */
export function sumDistribution(
  record: Partial<Record<string, number>> | undefined | null,
): number {
  if (!record) return 0;
  let total = 0;
  for (const v of Object.values(record)) {
    if (typeof v === 'number' && Number.isFinite(v)) total += v;
  }
  return total;
}
